import React, { useState, useEffect, useRef } from 'react';
import { MainMenu } from '../components/Menus/MainMenu';
import { HUD } from '../components/HUD/HUD';
import { LevelCompleteScreen } from '../components/Menus/LevelCompleteScreen';
import { GameOverScreen } from '../components/Menus/GameOverScreen';
import { InventoryMenu } from '../components/Menus/InventoryMenu';
import { PauseMenu } from '../components/Menus/PauseMenu';
import { DebugPanel } from '../components/Debug/DebugPanel';
import { StatsTracker } from '../components/HUD/StatsTracker';
import { CutsceneManager } from '../components/Cutscenes/CutsceneManager';
import type { Cutscene } from '../components/Cutscenes/CutsceneManager';
import { loadAllSoundEffects } from '../game/audio/SoundEffects';
import { loadAllMusicTracks, playMusicTrack, stopMusic, MusicTrack } from '../game/audio/MusicTracks';
import { audioManager } from '../engine/AudioManager';
import { upgradeSystem } from '../game/systems/UpgradeSystem';
import { objectiveSystem } from '../game/systems/ObjectiveSystem';
import { store } from '../state/store';
import { resetPlayer } from '../state/slices/playerSlice';
import { resetGame } from '../state/slices/gameSlice';
import { clearNotifications } from '../state/slices/uiSlice';
import { nightIntros, nightOutros } from '../game/data/cutscenes';
import { TestScene } from '../game/scenes/TestScene';
import { getGame } from '../engine/Game';
import { Night1 } from '../game/levels/Night1';
import { storyProgressionSystem } from '../game/systems/StoryProgressionSystem';

/**
 * Root React component for Five Nights at Khan's
 */
const App: React.FC = () => {
  const [showMainMenu, setShowMainMenu] = useState(true);
  const [gameStarted, setGameStarted] = useState(false);
  const [fadeOpacity, setFadeOpacity] = useState(0); // 0 = clear, 1 = black
  const [fadeInstant, setFadeInstant] = useState(false); // true = no CSS transition (instant reveal)
  const [hudOpacity, setHudOpacity] = useState(0);
  const [nightProgress, setNightProgress] = useState(0);
  const [currentNight, setCurrentNight] = useState(1);
  const [showLevelComplete, setShowLevelComplete] = useState(false);
  const [showInventory, setShowInventory] = useState(false);
  const [showPauseMenu, setShowPauseMenu] = useState(false);
  const [showDebugPanel, setShowDebugPanel] = useState(false); // Debug panel hidden by default
  const [currentCutscene, setCurrentCutscene] = useState<Cutscene | null>(null);
  const [showIntroCutscene, setShowIntroCutscene] = useState(false);
  const [showOutroCutscene, setShowOutroCutscene] = useState(false);
  const [debugStatus, setDebugStatus] = useState<string[]>([]);
  const [nightComplete, setNightComplete] = useState(false); // New flag for night completion
  const [gameSaved, setGameSaved] = useState(true); // Track if game has been saved
  const [hasSaveData, setHasSaveData] = useState(false); // Track if save data exists
  const [showMainMenuSettings, setShowMainMenuSettings] = useState(false); // Settings from main menu
  const [showGameOver, setShowGameOver] = useState(false); // Game over screen
  const [causeOfDeath, setCauseOfDeath] = useState('Caught by a robot');

  // Reference to current scene for cleanup
  const currentSceneRef = useRef<TestScene | null>(null);

  // Reference to the night progress timer so it can be cancelled on death
  const nightTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Track last saved progress to detect changes
  const lastSavedProgressRef = useRef<number>(0);

  // Store loaded save data to restore player state after scene loads
  const loadedSaveDataRef = useRef<any>(null);

  // Load sound effects and music on mount
  useEffect(() => {
    console.log('Loading audio...');
    loadAllSoundEffects();
    loadAllMusicTracks();
    console.log('Audio files loaded - music will play after user interaction (New Game)');
  }, []);

  // Check for existing save data on mount
  useEffect(() => {
    const checkSaveData = async () => {
      try {
        const saves = await window.electronAPI.listSaves();
        const hasData = saves && saves.length > 0;
        setHasSaveData(hasData);
        console.log(`Save data check: ${hasData ? 'Found' : 'Not found'} (${saves?.length || 0} saves)`);
      } catch (error) {
        console.error('Failed to check save data:', error);
        setHasSaveData(false);
      }
    };

    checkSaveData();
  }, []);

  // Restore player stats (NOT position - that's set during scene init) after loading a save
  useEffect(() => {
    if (!loadedSaveDataRef.current || !currentSceneRef.current) {
      return;
    }

    const saveData = loadedSaveDataRef.current;
    const scene = currentSceneRef.current;

    // Wait a bit for scene to be fully ready
    setTimeout(() => {
      if (scene.player && saveData.player) {
        console.log('Restoring player stats from save...');

        // Restore player stats (position was already set during scene init)
        scene.player.health = saveData.player.health || 100;
        scene.player.stamina = saveData.player.stamina || 100;
        scene.player.maxHealth = saveData.player.maxHealth || 100;
        scene.player.maxStamina = saveData.player.maxStamina || 100;
        scene.player.flashlightBattery = saveData.player.flashlightBattery || 100;
        scene.player.xp = saveData.player.xp || 0;
        scene.player.level = saveData.player.level || 1;

        console.log('✅ Player stats restored from save!');

        // Clear the loaded save data
        loadedSaveDataRef.current = null;
      }
    }, 300); // Wait 300ms for scene to fully initialize
  }, [currentSceneRef.current, gameStarted]);

  // Mark game as unsaved when night progress changes significantly (more than 1%)
  useEffect(() => {
    if (gameStarted && nightProgress > 0) {
      const progressDifference = Math.abs(nightProgress - lastSavedProgressRef.current);
      // Only mark as unsaved if progress changed by more than 1%
      if (progressDifference > 1) {
        setGameSaved(false);
      }
    }
  }, [nightProgress, gameStarted]);

  // Handle keyboard input for inventory (Tab or I key), debug panel (~ key), and pause menu (ESC key)
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Pause menu toggle (ESC key)
      if (e.key === 'Escape') {
        e.preventDefault();
        if (gameStarted && !showLevelComplete && !showInventory) {
          const newPauseState = !showPauseMenu;
          setShowPauseMenu(newPauseState);
          // ======== COPY FROM HERE ========
          (window as any).gamePaused = newPauseState;
          // ======== COPY TO HERE ========

          // Pause/Resume the game scene
          if (currentSceneRef.current) {
            if (newPauseState) {
              currentSceneRef.current.pause();
            } else {
              currentSceneRef.current.resume();
            }
          }

          console.log(`Pause menu ${newPauseState ? 'shown' : 'hidden'}`);
        }
      }

      // Inventory toggle
      if (e.key === 'Tab' || e.key === 'i' || e.key === 'I') {
        e.preventDefault();
        if (gameStarted && !showLevelComplete && !showPauseMenu) {
          setShowInventory(prev => !prev);
        }
      }

      // Debug panel toggle (~ or ` key)
      if (e.key === '~' || e.key === '`') {
        e.preventDefault();
        setShowDebugPanel(prev => !prev);
        console.log(`Debug panel ${!showDebugPanel ? 'shown' : 'hidden'}`);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [gameStarted, showLevelComplete, showInventory, showPauseMenu, showDebugPanel]);

  const handleContinue = async () => {
    console.log('Loading saved game...');

    try {
      // Load save from slot 1
      const saveData = await window.electronAPI.loadGame(1);

      if (!saveData) {
        alert('No save data found!');
        return;
      }

      console.log('Loaded save data:', saveData);
      console.log('Will spawn at position:', saveData.player?.position);

      // Store save data for restoring player stats after scene loads
      loadedSaveDataRef.current = saveData;

      setShowMainMenu(false);
      setCurrentNight(saveData.currentNight);

      // Start the saved night — startNight handles silencing all audio before gameplay music plays
      const spawnPos = saveData.player?.position;
      startNight(saveData.currentNight, spawnPos);

      // Player stats will be restored in the useEffect below
    } catch (error) {
      console.error('Failed to load save:', error);
      alert('Failed to load save data!');
    }
  };

  const handleMainMenuSettings = () => {
    console.log('Opening settings from main menu...');
    setShowMainMenuSettings(true);
  };

  const handleNewGame = () => {
    console.log('Starting new game...');

    // CRITICAL: Clear any loaded save data to prevent old progress from being restored
    loadedSaveDataRef.current = null;

    // Reset Redux state to initial values (health, stamina, XP, level, etc.)
    store.dispatch(resetPlayer());
    store.dispatch(resetGame());

    // Reset upgrade system to clear purchased upgrades
    upgradeSystem.resetUpgrades();

    // Clear objectives from any previous session
    objectiveSystem.clearObjectives();

    // Play music now that we have user interaction
    playMusicTrack(MusicTrack.PIANO_AMBIENT, true);
    console.log('Playing piano ambient music');

    setShowMainMenu(false);
    setCurrentNight(1); // Reset to night 1
    setNightProgress(0); // Reset progress
    setGameSaved(true); // No unsaved changes yet
    lastSavedProgressRef.current = 0; // Reset save tracking

    // Show intro cutscene for Night 1
    const introCutscene = nightIntros[1];
    if (introCutscene) {
      setCurrentCutscene(introCutscene);
      setShowIntroCutscene(true);
    } else {
      // If no cutscene, start game directly
      startNight(1);
    }
  };

  const startNight = async (nightNumber: number, spawnPosition?: { x: number; y: number }) => {
    console.log(`Starting Night ${nightNumber}...`);
    // Stop ALL audio immediately (piano ambient, any leftover SFX) before night loads
    audioManager.stopAll();
    // Instantly go black (no animation), then fade out to reveal gameplay when ready
    setFadeInstant(true);
    setFadeOpacity(1);
    setHudOpacity(0); // reset HUD to invisible before new night loads
    requestAnimationFrame(() => setFadeInstant(false)); // re-enable transition for the reveal
    if (spawnPosition) {
      console.log(`Will spawn player at:`, spawnPosition);
    }

    // Destroy previous scene if it exists
    if (currentSceneRef.current) {
      console.log('Destroying previous scene...');
      currentSceneRef.current.destroy();
      currentSceneRef.current = null;
    }

    setGameStarted(true);
    setNightProgress(0);
    setNightComplete(false); // Reset completion flag
    setShowIntroCutscene(false);
    setShowOutroCutscene(false);
    setCurrentCutscene(null);

    // Reset save tracking for new night
    setGameSaved(true); // New night starts with no unsaved changes
    lastSavedProgressRef.current = 0;

    // Clear previous objectives
    objectiveSystem.clearObjectives();

    // Wait for canvas to be rendered before initializing level
    setTimeout(async () => {
      try {
        const addDebug = (msg: string) => {
          console.log(msg);
          setDebugStatus(prev => [...prev, msg]);
        };

        addDebug('=== STARTING GAME INITIALIZATION ===');

        // Load TestScene with all the content
        addDebug(`Loading TestScene for Night ${nightNumber}...`);
        const testScene = new TestScene(nightNumber);
        await testScene.initialize();

        // Store reference for cleanup later
        currentSceneRef.current = testScene;

        // Set up death callback - show game over screen when player dies
        testScene.setOnPlayerDeath((cause: string) => {
          console.log(`💀 Player died on Night ${nightNumber}! Showing game over screen...`);
          // Stop the night timer so it can't trigger the victory sequence
          if (nightTimerRef.current) {
            clearInterval(nightTimerRef.current);
            nightTimerRef.current = null;
          }
          setCauseOfDeath(cause);
          fadeTransition(() => {
            if (currentSceneRef.current) {
              currentSceneRef.current.destroy();
              currentSceneRef.current = null;
            }
            setGameStarted(false);
            setShowGameOver(true);
          });
        });

        // If we have a spawn position (from loaded save), set it now
        if (spawnPosition && testScene.player) {
          testScene.player.position.x = spawnPosition.x;
          testScene.player.position.y = spawnPosition.y;
          testScene.player.velocity.x = 0;
          testScene.player.velocity.y = 0;
          console.log(`✅ Player spawned at saved position: (${spawnPosition.x}, ${spawnPosition.y})`);
        }

        addDebug('TestScene loaded successfully!');
        addDebug('You should now see: green player, red enemies, brown platforms, elevator, collectibles');

        setDebugStatus(prev => [...prev, '✅ Game ready! Use WASD/Arrows to move, Space to jump']);
        // Fade out from black to reveal gameplay (animated)
        setFadeInstant(false);
        setFadeOpacity(0);
        setTimeout(() => setHudOpacity(1), 400);
        playMusicTrack(MusicTrack.GAMEPLAY, true);
      } catch (error: any) {
        console.error('Failed to load game:', error);
        console.error('Error details:', error.message, error.stack);
        alert(`Failed to start game: ${error.message}\nCheck console for details.`);
      }
    }, 100);
  };

  // Night progress timer - like FNAF, progresses over time
  // Respects debug time scale for faster testing
  useEffect(() => {
    if (!gameStarted) return;

    // Night duration scales up each night — longer shift = more time for robots to hunt you
    // N1: 6min  N2: 6.5min  N3: 7min  N4: 7.5min  N5: 8min  N6: 8.5min  N7: 10min
    const nightDurations = [360000, 390000, 420000, 450000, 480000, 510000, 600000];
    const nightDuration = nightDurations[Math.min(currentNight - 1, 6)] ?? 360000;
    const updateInterval = 100; // Update every 100ms for smooth progress

    nightTimerRef.current = setInterval(() => {
      setNightProgress(prev => {
        // Get debug time scale (defaults to 1.0 if not set)
        const timeScale = (window as any).debugTimeScale || 1.0;

        // Calculate progress with time scale applied
        const progressPerUpdate = (100 / nightDuration) * updateInterval * timeScale;
        const newProgress = prev + progressPerUpdate;

        if (newProgress >= 99.9) {
          // Don't trigger victory if a game-over cutscene is already playing
          if (currentSceneRef.current?.isGameOverPending) return prev;
          if (nightTimerRef.current) {
            clearInterval(nightTimerRef.current);
            nightTimerRef.current = null;
          }
          console.log('🌅 Night completed! Stopping game and hiding UI...');

          // Destroy the scene to stop game loop and sounds
          if (currentSceneRef.current) {
            console.log('Destroying scene and stopping game loop...');
            currentSceneRef.current.destroy();
            currentSceneRef.current = null;
          }

          // Stop ALL audio (gameplay music + SFX) and reset AudioManager state
          audioManager.stopAll();
          // Start piano ambient immediately with no fade (everything was just silenced)
          playMusicTrack(MusicTrack.PIANO_AMBIENT, false);

          // Award XP for completing night (scales with night number)
          const baseXP = 100;
          const nightBonus = (currentNight - 1) * 50; // +50 XP per night
          const totalXP = baseXP + nightBonus;
          upgradeSystem.awardXP(totalXP, `Completed Night ${currentNight}`);

          // Show outro cutscene or level complete
          // These flags will hide the gameplay UI automatically
          const outroCutscene = nightOutros[currentNight];
          if (outroCutscene) {
            console.log('🎬 Showing outro cutscene');
            setCurrentCutscene(outroCutscene);
            setShowOutroCutscene(true);
          } else {
            console.log('✅ Showing level complete screen');
            setShowLevelComplete(true);
          }

          return 100;
        }
        return newProgress;
      });
    }, updateInterval);

    return () => {
      if (nightTimerRef.current) {
        clearInterval(nightTimerRef.current);
        nightTimerRef.current = null;
      }
    };
  }, [gameStarted, currentNight]);

  const handleContinueToNextLevel = () => {
    console.log('Continuing to next night...');
    setShowLevelComplete(false);
    setGameStarted(false);

    // Increment night number
    const nextNight = currentNight + 1;
    setCurrentNight(nextNight);

    // Check if there's a next night (max is 7)
    if (nextNight > 7) {
      console.log('All nights completed! Returning to main menu.');
      setShowMainMenu(true);
      setCurrentNight(1);
      return;
    }

    // Show intro cutscene for next night
    const introCutscene = nightIntros[nextNight];
    if (introCutscene) {
      setCurrentCutscene(introCutscene);
      setShowIntroCutscene(true);
    } else {
      // If no cutscene, start next night directly
      startNight(nextNight);
    }
  };

  const handleQuit = () => {
    // Add confirmation dialog
    const confirmed = confirm('Are you sure you want to quit?');
    if (confirmed) {
      console.log('Quitting game...');

      // IMMEDIATELY kill ALL audio - no fade, no delay
      audioManager.killAllAudio();

      // Clear all notifications
      store.dispatch(clearNotifications());

      // Clean up current scene if active
      if (currentSceneRef.current) {
        currentSceneRef.current.destroy();
        currentSceneRef.current = null;
      }

      // Small delay to ensure audio is completely killed before window closes
      setTimeout(() => {
        // Use Electron's proper window close IPC
        if (window.electronAPI?.window?.close) {
          window.electronAPI.window.close();
        } else {
          window.close();
        }
      }, 50);
    }
  };

  // Game over screen handlers
  const handleGameOverRestart = () => {
    console.log(`Restarting Night ${currentNight} after death...`);
    setShowGameOver(false);

    // Reset player state in Redux
    store.dispatch(resetPlayer());
    setNightProgress(0);

    stopMusic(false);                                
    playMusicTrack(MusicTrack.PIANO_AMBIENT, true);

    // Show intro cutscene for current night (restart from beginning)
    const introCutscene = nightIntros[currentNight];
    if (introCutscene) {
      setCurrentCutscene(introCutscene);
      setShowIntroCutscene(true);
    } else {
      // If no cutscene, restart night directly
      startNight(currentNight);
    }
  };

  const handleGameOverMainMenu = () => {
    console.log('Returning to main menu after death...');
    setShowGameOver(false);

    // Stop music (no fade)
    stopMusic(false);

    // Clear all notifications
    store.dispatch(clearNotifications());

    // Reset player state
    store.dispatch(resetPlayer());

    // Go to main menu
    setNightProgress(0);
    setShowMainMenu(true);
  };

  // Pause menu handlers
  const handlePauseResume = () => {
    console.log('Resuming game...');
    setShowPauseMenu(false);
    // ======== COPY FROM HERE ========
    (window as any).gamePaused = false;
    // ======== COPY TO HERE ========

    // Resume the game scene
    if (currentSceneRef.current) {
      currentSceneRef.current.resume();
    }
  };

  const handlePauseSave = async () => {
    console.log('Saving game...');

    try {
      // Get player data from current scene
      if (!currentSceneRef.current) {
        console.error('No active scene to save');
        alert('Failed to save: No active game session');
        return;
      }

      const scene = currentSceneRef.current;
      const player = scene.player;

      // Validate player data
      if (!player) {
        console.error('No player found in scene');
        alert('Failed to save: Player data not found');
        return;
      }

      console.log('Player data:', {
        health: player.health,
        stamina: player.stamina,
        position: player.position,
        xp: player.xp,
        level: player.level
      });

      // Get checkpoint data safely - this will be the spawn position
      let checkpointPosition = { x: player.position.x || 0, y: player.position.y || 0 };
      try {
        if (scene.checkpointSystem && typeof scene.checkpointSystem.getActiveCheckpoint === 'function') {
          const checkpoint = scene.checkpointSystem.getActiveCheckpoint();
          if (checkpoint && checkpoint.position) {
            // Use checkpoint position as spawn point
            checkpointPosition = { x: checkpoint.position.x, y: checkpoint.position.y };
            console.log('Saving with checkpoint position:', checkpointPosition);
          } else {
            console.log('No active checkpoint, using current position:', checkpointPosition);
          }
        }
      } catch (err) {
        console.warn('Could not get checkpoint data:', err);
      }

      // Prepare save data
      const saveData = {
        slotNumber: 1, // Auto-save to slot 1
        playerName: 'Player',
        gameState: {
          currentNight,
          currentLevel: `Night ${currentNight}`,
          player: {
            health: player.health || 100,
            stamina: player.stamina || 100,
            maxHealth: player.maxHealth || 100,
            maxStamina: player.maxStamina || 100,
            flashlightBattery: player.flashlightBattery || 100,
            xp: player.xp || 0,
            level: player.level || 1,
            position: checkpointPosition, // Save checkpoint position, not current position
          },
          lastCheckpoint: checkpointPosition, // Store checkpoint for reference
          playTime: 0, // TODO: Track play time
        },
      };

      console.log('Prepared save data:', JSON.stringify(saveData, null, 2));

      // Use IPC to save game
      console.log('Calling electronAPI.saveGame...');
      const result = await window.electronAPI.saveGame(saveData);
      console.log('Save result:', result);

      if (result) {
        console.log('✅ Game saved successfully!');
        setGameSaved(true);
        lastSavedProgressRef.current = nightProgress; // Remember the progress we just saved
        alert('✅ Game Saved Successfully!');

        // Refresh save data check
        const saves = await window.electronAPI.listSaves();
        setHasSaveData(saves && saves.length > 0);
      } else {
        console.error('❌ Save returned false');
        alert('❌ Failed to save game - Check console for details');
      }
    } catch (error: any) {
      console.error('❌ Error saving game:', error);
      console.error('Error stack:', error?.stack);
      alert(`❌ Error saving game: ${error?.message || 'Unknown error'}`);
    }
  };

  const handlePauseMainMenu = () => {
    console.log('Returning to main menu...');
    fadeTransition(() => {
      stopMusic(false);
      store.dispatch(clearNotifications());
      if (currentSceneRef.current) {
        currentSceneRef.current.destroy();
        currentSceneRef.current = null;
      }
      setShowPauseMenu(false);
      setGameStarted(false);
      setShowMainMenu(true);
      setNightProgress(0);
      setGameSaved(true);
    });
  };

  /** Fade to black, run callback, then instantly reveal (no fade-in) */
  const fadeTransition = (callback: () => void, holdMs = 120) => {
    setFadeInstant(false); // animated fade out to black
    setFadeOpacity(1);
    setTimeout(() => {
      callback();
      setTimeout(() => {
        setFadeInstant(true);  // snap overlay away — no fade-in
        setFadeOpacity(0);
        setTimeout(() => setFadeInstant(false), 50);
      }, holdMs);
    }, 400); // 400ms to fully black
  };

  return (
    <div style={{
      width: '100vw',
      height: '100vh',
      backgroundColor: '#000000'
    }}>
      {/* Main Menu */}
      {showMainMenu && !showMainMenuSettings && (
        <MainMenu
          onNewGame={handleNewGame}
          onContinue={handleContinue}
          onSettings={handleMainMenuSettings}
          onQuit={handleQuit}
          hasSaveData={hasSaveData}
        />
      )}

      {/* Main Menu Settings - Same UI as pause menu settings */}
      {showMainMenu && showMainMenuSettings && (
        <PauseMenu
          visible={true}
          isSaved={true}
          showSettingsOnly={true}
          onResume={() => setShowMainMenuSettings(false)}
          onSettings={() => setShowMainMenuSettings(false)}
          onMainMenu={() => setShowMainMenuSettings(false)}
        />
      )}

      {/* Game Canvas - only shown when game started and not showing completion screens */}
      {gameStarted && !showLevelComplete && !showOutroCutscene && (
        <>
          {/* Debug: Initialization Status - ONLY show when debug panel is expanded */}
          {showDebugPanel && (
            <div style={{
              position: 'fixed',
              bottom: '10px',
              left: '10px',
              background: 'rgba(0, 0, 0, 0.9)',
              color: '#0f0',
              padding: '15px',
              fontSize: '12px',
              zIndex: 10000,
              border: '2px solid #0f0',
              fontFamily: 'monospace',
              maxWidth: '400px',
              maxHeight: '300px',
              overflowY: 'auto'
            }}>
              <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '10px', color: '#ff0' }}>
                DEBUG STATUS
              </div>
              {debugStatus.length === 0 ? (
                <div>Waiting for initialization...</div>
              ) : (
                debugStatus.map((msg, idx) => (
                  <div key={idx} style={{ marginBottom: '5px' }}>
                    {idx + 1}. {msg}
                  </div>
                ))
              )}
            </div>
          )}

          <canvas id="game-canvas" tabIndex={1} style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: (showLevelComplete || showOutroCutscene) ? 'none' : 'block',
            outline: 'none',
          }} />

          {/* HUD Overlay - hide when night completes, paused, or inventory open */}
          <div style={{
            display: (showPauseMenu || showInventory) ? 'none' : 'block',
            opacity: hudOpacity,
            transition: 'opacity 1s ease',
          }}>
            {!showLevelComplete && !showOutroCutscene && nightProgress < 100 && (
              <HUD
                nightNumber={currentNight}
                nightProgress={nightProgress}
                showMiniMap={true}
                showObjectives={true}
                showInteractionPrompt={true}
                showHealth={true}
                showStamina={true}
                showBattery={true}
                showClock={true}
                showProgress={true}
              />
            )}
          </div>
        </>
      )}

      {/* Level Complete Screen */}
      {showLevelComplete && (
        <LevelCompleteScreen
          nightNumber={currentNight}
          onContinue={handleContinueToNextLevel}
          collectiblesCollected={storyProgressionSystem.getProgress().collectedItems.length}
          tasksCompleted={objectiveSystem.getStats().completedObjectives}
          totalCollectibles={storyProgressionSystem.getProgress().totalCollectibles}
          totalTasks={objectiveSystem.getStats().totalObjectives}
        />
      )}

      {/* Game Over Screen */}
      <GameOverScreen
        visible={showGameOver}
        nightNumber={currentNight}
        causeOfDeath={causeOfDeath}
        onRestart={handleGameOverRestart}
        onMainMenu={handleGameOverMainMenu}
      />

      {/* Inventory Menu */}
      {gameStarted && !showLevelComplete && !showOutroCutscene && nightProgress < 100 && (
        <InventoryMenu
          visible={showInventory}
          onClose={() => setShowInventory(false)}
        />
      )}

      {/* Pause Menu */}
      {gameStarted && !showLevelComplete && !showOutroCutscene && nightProgress < 100 && (
        <PauseMenu
          visible={showPauseMenu}
          isSaved={gameSaved}
          onResume={handlePauseResume}
          onSave={handlePauseSave}
          onSettings={() => console.log('Settings in pause menu')}
          onMainMenu={handlePauseMainMenu}
        />
      )}

      {/* Stats Tracker - only show when debug panel is visible */}
      {gameStarted && !showLevelComplete && !showOutroCutscene && nightProgress < 100 && showDebugPanel && <StatsTracker />}

      {/* Debug Panel - hide during cutscenes and level complete */}
      {!showLevelComplete && !showOutroCutscene && nightProgress < 100 && (
        <DebugPanel
          visible={showDebugPanel}
          onToggle={() => setShowDebugPanel(prev => !prev)}
          nightProgress={nightProgress}
        />
      )}

      {/* Cutscenes - Intro */}
      {showIntroCutscene && currentCutscene && (
        <CutsceneManager
          cutscene={currentCutscene}
          onComplete={() => {
            console.log('Intro cutscene complete, starting night');
            setFadeOpacity(1);
            setTimeout(() => {
              setShowIntroCutscene(false);
              startNight(currentNight);
            }, 400);
          }}
          onSkip={() => {
            console.log('Intro cutscene skipped');
            setFadeOpacity(1);
            setTimeout(() => {
              setShowIntroCutscene(false);
              startNight(currentNight);
            }, 400);
          }}
        />
      )}

      {/* Cutscenes - Outro */}
      {showOutroCutscene && currentCutscene && (
        <CutsceneManager
          cutscene={currentCutscene}
          onComplete={() => {
            console.log('Outro cutscene complete, showing level complete screen');
            setShowOutroCutscene(false);
            setShowLevelComplete(true);
          }}
          onSkip={() => {
            console.log('Outro cutscene skipped');
            setShowOutroCutscene(false);
            setShowLevelComplete(true);
          }}
        />
      )}

      {/* Scene transition fade overlay — sits above everything */}
      <div style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: '#000000',
        opacity: fadeOpacity,
        transition: fadeInstant ? 'none' : 'opacity 0.4s ease',
        pointerEvents: 'none',
        zIndex: 99999,
      }} />
    </div>
  );
};

export default App;
